import React, { useState } from 'react';
import { I18n, translate } from '@td/shared_utils';
import { useQuery } from '@apollo/client';
import { get } from 'lodash';
import { CgArrowLongLeft } from 'react-icons/cg';
import { CgArrowLongRight } from 'react-icons/cg';

import Loader from '../../../lib/loader';
import DRUG_LIST_MANAGED_DRUGS_QUERY from '../../queries/drug-list-managed-drugs.graphql';
import ManagedMedicationsTableHeader from './managed-medications-table-header';
import ManagedMedicationsTableRow from './managed-medications-table-row';
import useCachedQueryData from '../../../lib/hooks/useCachedQueryData';
import SearchPager from '../../../utility/search-pager';
import { DownloadIcon } from '../../../icons';

const MANAGED_MEDICATIONS_DRUG_ID_TYPE = 'DRUGID';
const MANAGED_MEDICATIONS_PER_PAGE = 20;

const DOWNLOAD_XLS_PATH = '/medication_formularies/fetch_formularies_xls';

const ManagedMedicationsTable = () => {
  const [page, setPage] = useState(1);
  const [sortParams, setSortParams] = useState({ order: 'ASC', sortBy: 'drug_name' });

  const { data, loading, error } = useQuery(DRUG_LIST_MANAGED_DRUGS_QUERY, {
    variables: {
      searchParams: { drugIdType: MANAGED_MEDICATIONS_DRUG_ID_TYPE },
      sortParams,
      page,
      perPage: MANAGED_MEDICATIONS_PER_PAGE
    }
  });
  const [cachedData] = useCachedQueryData(data);

  const changeSort = newSortParams => {
    setPage(1);

    setSortParams(newSortParams);
  };

  const restrictedMedications = get(cachedData, ['drugListManagedDrugs', 'items']) || [];

  return (
    <section className="managed-medications-table">
      {loading && <Loader />}
      <div className="download-data-button-wrapper">
        <a href={DOWNLOAD_XLS_PATH} type="application/vnd.ms-excel" target="_blank" className="download-data-button">
          <I18n scope="medication_formularies.table" text="download_button" />
          <DownloadIcon />
        </a>
      </div>

      <h3>
        <I18n scope="medication_formularies.table" text="title" />
      </h3>
      <table>
        <thead>
          <tr>
            <th width="20%">
              <ManagedMedicationsTableHeader headerName="drug_id" sortParams={sortParams} changeSort={changeSort} />
            </th>
            <th width="40%">
              <ManagedMedicationsTableHeader headerName="drug_name" sortParams={sortParams} changeSort={changeSort} />
            </th>
            <th width="25%">
              <ManagedMedicationsTableHeader
                headerName="managed_drug_lists"
                sortParams={sortParams}
                changeSort={changeSort}
              />
            </th>
            <th width="15%">
              <ManagedMedicationsTableHeader headerName="actions" isSortable={false} />
            </th>
          </tr>
        </thead>
        <tbody>
          {error ? (
            <tr>
              <td colSpan="4">
                <I18n scope="medication_formularies.table" text="error" />
              </td>
            </tr>
          ) : (
            restrictedMedications.map(medication => (
              <ManagedMedicationsTableRow key={medication.drugId} medication={medication} />
            ))
          )}
        </tbody>
      </table>
      <SearchPager
        page={page}
        perPage={MANAGED_MEDICATIONS_PER_PAGE}
        totalEntries={get(cachedData, ['drugListManagedDrugs', 'totalEntries'])}
        setPage={setPage}
        prevPageText={translate(null, 'medication_formularies.table.pagination', 'previous')}
        nextPageText={translate(null, 'medication_formularies.table.pagination', 'next')}
        firstPageText={<CgArrowLongLeft className="pagination-arrows" />}
        lastPageText={<CgArrowLongRight className="pagination-arrows" />}
      />
    </section>
  );
};

export default ManagedMedicationsTable;
